import { useEffect, useMemo, useState } from 'react';
import { getShareInfoApi } from 'api/client';
import useClientErrorHandler from 'client/hooks/useClientErrorHandler';

// 全域快取
const globalCache = new Map();
// 防止重複請求
const globalPromises = new Map();

// 定義參數類型
const PARAM_TYPE = {
    PROMO: 'promo',
    LEGACY: 'legacy'
};

// 定義參數正則表達式
const PARAM_REGEX = {
    [PARAM_TYPE.PROMO]: /\{\{promo:([^}]+)\}\}/g,
    [PARAM_TYPE.LEGACY]: {
        AT: /@([a-zA-Z0-9_]+)/g,
        SYSTEM: /\{\{system:([a-zA-Z0-9_]+)\}\}/g
    }
};

// 定義需要 API 調用的參數
export const API_REQUIRED_PARAMS = new Set(['username', 'result:title']);

// 生成快取 key
const generateCacheKey = (promoUuid, entryUuid, referralCode) => {
    return `${promoUuid}-${entryUuid || 'null'}-${referralCode || 'null'}`;
};

/**
 * 解析 content 中的動態參數
 * @param {string} content - 包含動態參數的內容
 * @param {boolean} enableLegacyConversion - 是否啟用舊格式轉換
 * @returns {Object} 解析後的參數
 * example:
 * {
 *   promo: ['username', 'result:title'],
 *   legacy: ['username']
 * }
 */
export const parseContentDynamicParameters = (content, options = {}) => {
    const { enableLegacyConversion = true } = options;

    const result = { promo: [], legacy: [] };

    if (!content) return result;

    // 解析 promo 參數
    const promoMatches = [...content.matchAll(PARAM_REGEX[PARAM_TYPE.PROMO])];
    result.promo = [...new Set(promoMatches.map(match => match[1]))];

    // 解析 legacy 參數（如果啟用）
    if (enableLegacyConversion) {
        const atMatches = [...content.matchAll(PARAM_REGEX[PARAM_TYPE.LEGACY].AT)];
        const systemMatches = [...content.matchAll(PARAM_REGEX[PARAM_TYPE.LEGACY].SYSTEM)];

        result.legacy = [
            ...new Set([
                ...atMatches.map(match => match[1]),
                ...systemMatches.map(match => match[1])
            ])
        ];
    }

    return result;
};

/**
 * 轉換舊格式為新格式
 * @param {string} content - 包含舊格式參數的內容
 * @returns {string} 轉換後的內容
 */
export const convertLegacyToPromo = content => {
    if (!content) return content;

    let result = content;
    result = result.replace(PARAM_REGEX[PARAM_TYPE.LEGACY].AT, '{{promo:$1}}');
    result = result.replace(PARAM_REGEX[PARAM_TYPE.LEGACY].SYSTEM, '{{promo:$1}}');

    return result;
};

/**
 * 轉換為客戶端數據
 * @param {string} content - 包含動態參數的內容
 * @param {Object} promoData - promo 數據
 * example:
 * {
 *   promo: {
 *     username: 'John Doe',
 *     result:title: '歡迎 {{promo:username}} 加入發燒互動'
 *   }
 * }
 * @param {number} maxDepth - 最大遞歸深度，防止無限循環
 * @returns {string} 替換後的內容
 */
export const convertToClientData = (content, promoData, maxDepth = 3) => {
    if (!content || maxDepth <= 0) return content;

    const originalContent = content;

    // match: 是原始的動態參數，比如 {{promo:username}}
    // paramPath: Ex: username、result:title
    let result = content.replace(PARAM_REGEX[PARAM_TYPE.PROMO], (match, paramPath) => {
        // 如果「動態參數」沒有資料，則替換空值
        return promoData?.[paramPath] || '';
    });

    // 如果內容有變化且還包含動態參數，繼續遞歸處理
    // Ex: result 是 "歡迎 {{promo:username}} 加入發燒互動"
    if (result !== originalContent && result.includes('{{promo:')) {
        return convertToClientData(result, promoData, maxDepth - 1);
    }

    return result;
};

/**
 * 將 API 回應轉換為 hook 需要的資料結構
 * @param {Object} apiResponse - getShareInfoApi 的回應
 * @param {string[]} requiredParams - 需要的參數列表
 * @returns {Object} 轉換後的資料結構
 */
export const convertApiResponseToPromoData = (apiResponse, requiredParams) => {
    if (!apiResponse) return {};

    const promoData = {};

    // 先提取所有基礎資料
    requiredParams.forEach(paramPath => {
        switch (paramPath) {
            // 未來這邊可以輕鬆添加更多參數處理
            case 'username':
                promoData[paramPath] = apiResponse.shareMemberFullname || '';
                break;

            case 'result:title':
                promoData[paramPath] = apiResponse.share?.title || '';
                break;

            default:
                promoData[paramPath] = '';
        }
    });

    return promoData;
};

/**
 * 共享的 API 調用函數 - 帶快取機制和 AbortController 支持
 * @param {string} promoUuid
 * @param {string} entryUuid
 * @param {string} referralCode
 * @param {AbortSignal} signal - AbortController 的 signal
 * @returns {Promise}
 */
const fetchShareInfoWithCache = async (promoUuid, entryUuid, referralCode, signal) => {
    const cacheKey = generateCacheKey(promoUuid, entryUuid, referralCode);

    // 檢查快取
    if (globalCache.has(cacheKey)) {
        const cachedData = globalCache.get(cacheKey);
        // 如果請求被取消，但快取中有資料，仍然可以返回快取資料
        return cachedData;
    }

    // 檢查是否已有進行中的請求
    if (globalPromises.has(cacheKey)) {
        return globalPromises.get(cacheKey);
    }

    // 檢查是否已經取消
    if (signal?.aborted) {
        throw new Error('Request was aborted before starting');
    }

    // 建立新的請求
    const promise = getShareInfoApi(
        {
            promoUUID: promoUuid,
            entryUUID: entryUuid,
            referralCode
        },
        { signal }
    ) // 將 signal 傳遞給 API 調用
        .then(response => {
            const result = {
                data: response?.payload,
                success: true,
                timestamp: Date.now()
            };

            // 只有在請求沒有被取消的情況下才儲存快取
            if (!signal?.aborted) {
                globalCache.set(cacheKey, result);
            }

            // 移除進行中的請求
            globalPromises.delete(cacheKey);

            return result;
        })
        .catch(error => {
            // 移除進行中的請求
            globalPromises.delete(cacheKey);

            // 如果是取消請求，直接拋出錯誤，不儲存快取
            // 通常是在當元件在 API 調用完成前就卸載，導致請求被取消
            // 此做法可以預防 "Warning: Can't perform a React state update on an unmounted component." 警告
            if (error.name === 'AbortError') {
                throw error;
            }

            const result = {
                error,
                success: false,
                timestamp: Date.now()
            };

            // 錯誤也快取一小段時間，避免重複失敗請求
            globalCache.set(cacheKey, result);

            return result;
        });

    // 儲存進行中的請求
    globalPromises.set(cacheKey, promise);

    return promise;
};

/**
 * 清除快取的工具函數
 */
export const clearPromoCache = (promoUuid, entryUuid, referralCode) => {
    const cacheKey = generateCacheKey(promoUuid, entryUuid, referralCode);
    globalCache.delete(cacheKey);
    globalPromises.delete(cacheKey);
};

/**
 * 清除所有快取
 */
export const clearAllPromoCache = () => {
    globalCache.clear();
    globalPromises.clear();
};

/**
 * 遞歸解析內容中的所有動態參數（包括嵌套的）
 * @param {string} content - 包含動態參數的內容
 * @param {Object} apiResponse - API 回應數據
 * @param {Set} visited - 已訪問的參數集合，防止無限循環
 * @returns {Set} 所有需要的參數集合
 */
const getAllNestedParams = (content, apiResponse, visited = new Set()) => {
    if (!content) return visited;

    const paramIds = parseContentDynamicParameters(content);
    const allParams = [...paramIds.promo, ...paramIds.legacy];

    for (const param of allParams) {
        if (!visited.has(param)) {
            visited.add(param);

            // 獲取這個參數對應的值
            let paramValue = '';
            switch (param) {
                case 'username':
                    paramValue = apiResponse.shareMemberFullname || '';
                    break;
                case 'result:title':
                    paramValue = apiResponse.share?.title || '';
                    break;
                default:
                    paramValue = '';
            }

            // 如果參數值中還包含動態參數，遞歸解析
            if (paramValue && paramValue.includes('{{promo:')) {
                getAllNestedParams(paramValue, apiResponse, visited);
            }
        }
    }

    return visited;
};

/**
 * 動態參數處理 Hook - 簡化版本
 * @param {string} content - 包含動態參數的內容
 * @param {Object} options - 選項
 * @param {string} options.promoUuid - 活動 UUID
 * @param {string} options.entryUuid - 參加者 UUID
 * @param {string} options.referralCode - 邀請碼
 * @param {boolean} options.enableLegacyConversion - 是否啟用舊格式轉換
 * @param {boolean} options.cacheTime - 快取時間
 * @returns {Object} { clientData: 處理後的客戶端數據, loading: 載入狀態 }
 */
export default function usePromoDynamicParameters(
    content,
    {
        promoUuid,
        entryUuid,
        referralCode,
        enableLegacyConversion = false,
        cacheTime = 1000 * 60 * 5
    } = {}
) {
    const [apiPromoData, setApiPromoData] = useState({});
    const [loading, setLoading] = useState(false);
    const errHandler = useClientErrorHandler();

    // 處理內容格式轉換
    const processedContent = useMemo(() => {
        if (!content) return content;
        return enableLegacyConversion ? convertLegacyToPromo(content) : content;
    }, [content, enableLegacyConversion]);

    // 檢查是否需要 API 調用
    const needsApi = useMemo(() => {
        if (!processedContent || !promoUuid) return false;

        const paramIds = parseContentDynamicParameters(processedContent, {
            enableLegacyConversion
        });
        const allParams = [...paramIds.promo, ...paramIds.legacy];
        const apiRequiredParams = allParams.filter(param => API_REQUIRED_PARAMS.has(param));

        return apiRequiredParams.length > 0;
    }, [processedContent, promoUuid, enableLegacyConversion]);

    useEffect(() => {
        if (!needsApi) {
            setApiPromoData({});
            setLoading(false);
            return;
        }

        const abortController = new AbortController();

        const fetchData = async () => {
            try {
                setLoading(true);

                // 檢查快取是否過期
                const cacheKey = generateCacheKey(promoUuid, entryUuid, referralCode);
                const cachedData = globalCache.get(cacheKey);

                if (cachedData && Date.now() - cachedData.timestamp > cacheTime) {
                    globalCache.delete(cacheKey);
                }

                // 使用快取機制獲取資料
                const response = await fetchShareInfoWithCache(
                    promoUuid,
                    entryUuid,
                    referralCode,
                    abortController.signal
                );

                // 檢查是否已被取消
                if (abortController.signal.aborted) return;

                if (response.success) {
                    // 遞歸解析所有需要的參數（包括嵌套的）
                    const allNeededParams = getAllNestedParams(processedContent, response.data);
                    const allParams = Array.from(allNeededParams);

                    // 轉換 API 回應為 promo 數據
                    const convertedData = convertApiResponseToPromoData(response.data, allParams);
                    setApiPromoData(convertedData);
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    return; // 請求被取消，不處理
                }
                console.error('Error fetching promo data:', error);
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [
        needsApi,
        promoUuid,
        entryUuid,
        referralCode,
        processedContent,
        enableLegacyConversion,
        cacheTime,
        errHandler
    ]);

    return {
        clientData: useMemo(
            () =>
                processedContent && !loading
                    ? convertToClientData(processedContent, apiPromoData)
                    : processedContent,
            [processedContent, apiPromoData, loading]
        ),
        loading
    };
}
